import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PageElement } from '@/api/types/stowed-element'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION, docPxRectToCentered } from '@/lib/canvas/document-projection'
import { BuildStowedShapeOptions } from '../types/build-stowed-shape-options'

export function buildBase(
  shape: ShapeData,
  options?: BuildStowedShapeOptions,
  projection: DocumentProjection = IDENTITY_PROJECTION
): PageElement {
  // NSD anchors element geometry at its center (`AR="CTR"` — the only anchor real NSD
  // documents use; see read-page-element-editor-rect.ts), while ShapeData stores the
  // top-left corner. Shift to the center point before projecting to the wire frame.
  const docRect = docPxRectToCentered(
    {
      x: shape.x + shape.width / 2,
      y: shape.y + shape.height / 2,
      width: shape.width,
      height: shape.height,
    },
    projection
  )
  const base: PageElement = {
    x: docRect.x,
    y: docRect.y,
    width: docRect.width,
    height: docRect.height,
    rotation: shape.rotation ?? 0,
    isLocked: shape.locked === true,
    isFrozen: shape.frozen === true,
    ignoreTextWrap: shape.ignoreTextWrap === true,
    transparency:
      typeof shape.opacity === 'number' && shape.opacity < 1
        ? Math.round((1 - shape.opacity) * 100)
        : 0,
    anchorRef: 'CTR',
    // Text-link chain membership key for a shape acting as a text frame (WMPCM-7988) — only emit
    // a real (>0) uid, so an unlinked shape stays uid-less on the wire rather than persisting a
    // meaningless 0.
    ...(typeof shape.uid === 'number' && shape.uid > 0 ? { uid: shape.uid } : {}),
  }

  if (options?.includeVisibleForRotate) {
    base.visibleX = docRect.x
    base.visibleY = docRect.y
    base.visibleWidth = docRect.width
    base.visibleHeight = docRect.height
  }

  return base
}
